import React, { useState, useEffect } from "react";
import { useAuth } from "../../firebase/AuthProvider";
import {
  followStatement,
  unfollowStatement,
  getStatementFollower,
} from "../../firebase/statementfollowing";
import SignInDialog from "../signin/SigninDialog";
import { Statement } from "../../firebase/statements";
import { useTranslation } from "react-i18next";
import { Button } from "@material-ui/core";
import FollowIcon from "@material-ui/icons/FavoriteBorder";
import FollowingIcon from "@material-ui/icons/Favorite";
import Tooltip from "../shared/Tooltip";
import { colors } from "../../theme";

interface Props {
  statement: Statement;
  className: string;
}

export default function Following({ statement, className }: Props) {
  const { t } = useTranslation();
  const [following, setFollowing] = useState<boolean>(false);
  const { isSignedIn, user } = useAuth();
  const [openSignInDialog, setOpenSignInDialog] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!user || !statement) return;
    const unsubscribe = getStatementFollower(
      statement.id,
      user.id,
      setFollowing
    );
    return () => unsubscribe();
  }, [user, statement]);

  async function follow() {
    if (!statement || !user) return;
    await followStatement(statement, user);
  }

  async function unfollow() {
    if (!statement || !user) return;
    await unfollowStatement(statement.id, user.id);
  }

  async function handleFollowUnfollowClick(
    followOrUnfollow: () => Promise<void>
  ) {
    if (!isSignedIn) {
      setOpenSignInDialog(true);
      return;
    }

    setIsLoading(true);
    await followOrUnfollow();
    setIsLoading(false);
  }

  return (
    <>
      <SignInDialog
        open={openSignInDialog}
        onClose={() => setOpenSignInDialog(false)}
      />
      {following ? (
        <AlreadyFollowingButton
          text={t("following.favorite")}
          textMouseOver={t("following.favorite")}
          handleClick={async () => await handleFollowUnfollowClick(unfollow)}
          disabled={isLoading}
          className={className}
        />
      ) : (
        <NotYetFollowingButton
          text={t("following.favorite")}
          handleClick={async () => await handleFollowUnfollowClick(follow)}
          disabled={isLoading}
          className={className}
        />
      )}
    </>
  );
}

interface FollowingButtonProps {
  text: string;
  textMouseOver?: string;
  handleClick: () => void;
  disabled: boolean;
  className: any;
}

function AlreadyFollowingButton({
  text,
  handleClick,
  disabled,
  className,
}: FollowingButtonProps) {
  return (
    <Tooltip title={text}>
      <Button
        onClick={handleClick}
        disabled={disabled}
        startIcon={<FollowingIcon style={{ color: colors.FOLLOW_ICON }} />}
        aria-label={text}
        className={className}
      >
        {text}
      </Button>
    </Tooltip>
  );
}

function NotYetFollowingButton({
  text,
  handleClick,
  disabled,
  className,
}: FollowingButtonProps) {
  return (
    <Tooltip title={text}>
      <Button
        onClick={handleClick}
        disabled={disabled}
        startIcon={<FollowIcon style={{ color: colors.FOLLOW_ICON }} />}
        aria-label={text}
        className={className}
      >
        {text}
      </Button>
    </Tooltip>
  );
}
